import { Backdrop } from 'components/Backdrop';
import { Snackbar } from 'components/Snackbar';
import { createContext, ReactNode, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppointmentService } from 'services';
import { ITreatmentInQueue, PetStateProps, Symptoms } from 'types';

import { AlertColor } from '@mui/material';
import { useLoader } from 'hooks/utilHooks';

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
}

export interface ITreatmentInQueueContext {
  loading: boolean;
  pet: PetStateProps;
  petSymptoms: Symptoms[];
  treatmentQueueId: string | null;
  handleGoBack: () => Promise<void>;
  acceptTreatment: () => Promise<void>;
  getTreatmentInQueue: () => Promise<void>;
  generalClassification: Symptoms['classification'] | null;
}

export const TreatmentInQueueContext = createContext<ITreatmentInQueueContext>(
  {} as ITreatmentInQueueContext
);

type Props = {
  children: ReactNode;
};

export const TreatmentInQueueProvider = ({ children }: Props) => {
  const history = useHistory();

  const [treatmentQueueId, setTreatmentQueueId] = useState<string | null>(null);
  const [treatmentInQueue, setTreatmentInQueue] = useState<ITreatmentInQueue>(
    {} as ITreatmentInQueue
  );

  const { loading, showLoader, hideLoader } = useLoader();

  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });

  const getTreatmentInQueue = async () => {
    try {
      const treatment = await AppointmentService.getAppointmentInfo();

      setTreatmentQueueId(treatment._id);
      setTreatmentInQueue(treatment);
    } catch (error) {
      setSnackbar({
        message: 'Não foi possível pegar as informações do atendimento',
        variant: 'error',
      });
      setTimeout(() => {
        handleGoBack();
      }, 5000);
    }
  };

  const handleGoBack = async () => {
    try {
      const response = await AppointmentService.changeStatus();
      if (response.status === 200) {
        history.goBack();
      }
    } catch (error) {
      if (error instanceof Error) {
        history.goBack();
      }
      setSnackbar({
        message: 'Ocorreu um erro ao voltar para o painel de atendimentos.',
        variant: 'error',
      });
    }
  };

  const acceptTreatment = async () => {
    try {
      showLoader();
      if (!treatmentQueueId) {
        throw new Error('Não foi possível iniciar o atendimento');
      }

      const response = await AppointmentService.startAppointment(
        treatmentQueueId
      );

      if (response.status === 200) {
        history.push('/vet/dashboard/video-call');
      }
    } catch (error) {
      if (error instanceof Error) {
        setSnackbar({
          message: 'Não foi possível inicar o atendimento',
          variant: 'error',
        });
      }
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    getTreatmentInQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TreatmentInQueueContext.Provider
      value={{
        loading,
        pet: treatmentInQueue.pet,
        petSymptoms: treatmentInQueue.symptoms,
        generalClassification: treatmentInQueue.generalClassification,
        treatmentQueueId,
        handleGoBack,
        acceptTreatment,
        getTreatmentInQueue,
      }}
    >
      {children}

      <Snackbar
        text={snackbar.message}
        snackbarOpen={Boolean(snackbar.message)}
        setSnackbarOpen={() => {
          setSnackbar({
            message: '',
          });
        }}
        severityType={snackbar.variant}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />

      <Backdrop open={loading} />
    </TreatmentInQueueContext.Provider>
  );
};

